import { useState } from "react";
import { submitInquiry } from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function InquiryForm({ tour = null, compact = false }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    preferred_date: "",
    preferred_time: "",
    group_size: "",
    message: tour ? `I'd like to know more about "${tour.title}".` : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitInquiry({
        ...form,
        tour_slug: tour?.slug,
        tour_title: tour?.title,
      });
      toast.success("Thanks — we'll be in touch shortly.");
      setDone(true);
      setForm({ name: "", email: "", preferred_date: "", preferred_time: "", group_size: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-[var(--border)] bg-white p-8" data-testid="inquiry-success">
        <div className="eyebrow text-[var(--pine)] mb-3">Message sent</div>
        <h3 className="font-display text-3xl mb-3">Talk soon.</h3>
        <p className="text-[var(--ink-soft)]">
          Your inquiry is in. We typically reply within a few hours during the day in Riga.
        </p>
        <button
          onClick={() => setDone(false)}
          className="btn-ghost mt-6 text-sm"
          data-testid="inquiry-send-another"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
      <div className={compact ? "" : "grid grid-cols-1 sm:grid-cols-2 gap-6"}>
        <div>
          <label className="field-label" htmlFor="name">Your name</label>
          <input
            id="name"
            name="name"
            required
            minLength={2}
            value={form.name}
            onChange={handleChange}
            className="field-input"
            placeholder="Jane Doe"
            data-testid="inquiry-input-name"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={form.email}
            onChange={handleChange}
            className="field-input"
            placeholder="you@example.com"
            data-testid="inquiry-input-email"
          />
        </div>
      </div>

      <div className={compact ? "" : "grid grid-cols-1 sm:grid-cols-2 gap-6"}>
        <div>
          <label className="field-label" htmlFor="preferred_date">Preferred date</label>
          <input
            id="preferred_date"
            name="preferred_date"
            type="date"
            value={form.preferred_date}
            onChange={handleChange}
            className="field-input"
            data-testid="inquiry-input-date"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="preferred_time">Preferred time</label>
          <input
            id="preferred_time"
            name="preferred_time"
            value={form.preferred_time}
            onChange={handleChange}
            className="field-input"
            placeholder="e.g. 18:00 or evening"
            data-testid="inquiry-input-time"
          />
        </div>
      </div>

      <div className={compact ? "" : "grid grid-cols-1 sm:grid-cols-2 gap-6"}>
        <div>
          <label className="field-label" htmlFor="group_size">Group size</label>
          <input
            id="group_size"
            name="group_size"
            value={form.group_size}
            onChange={handleChange}
            className="field-input"
            placeholder="e.g. 4 adults"
            data-testid="inquiry-input-group"
          />
        </div>
      </div>

      <div>
        <label className="field-label" htmlFor="message">Message</label>
        <textarea
          id="message"
          name="message"
          required
          minLength={5}
          rows={4}
          value={form.message}
          onChange={handleChange}
          className="field-input"
          placeholder="Tell us a little about your group, any questions, etc."
          data-testid="inquiry-input-message"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary disabled:opacity-60"
        data-testid="inquiry-submit"
      >
        {submitting ? "Sending..." : "Send inquiry"} <Send size={16} />
      </button>
    </form>
  );
}
